import { useState } from 'react';
import type { UserQuestions } from '../../store/chat';
import { SpinnerIcon, ChatBubbleIcon, ChevronLeftIcon, ArrowRightIcon } from '../ui/icons';

interface QuestionCardProps {
  questions: UserQuestions;
  onAnswer: (answers: Record<string, string>) => void;
  isSubmitting: boolean;
}

export function QuestionCard({ questions, onAnswer, isSubmitting }: QuestionCardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [otherSelected, setOtherSelected] = useState<Record<string, boolean>>({});
  const [otherText, setOtherText] = useState<Record<string, string>>({});

  const totalSteps = questions.questions.length;
  const currentQuestion = questions.questions[currentStep];
  const isLastStep = currentStep === totalSteps - 1;

  const canProceed = () => {
    if (!currentQuestion) return false;
    if (currentQuestion.required === false) return true;

    if (currentQuestion.type === 'select') {
      return answers[currentQuestion.id] || (otherSelected[currentQuestion.id] && otherText[currentQuestion.id]?.trim());
    }
    return answers[currentQuestion.id]?.trim();
  };

  const handleSelectOption = (optionId: string) => {
    if (!currentQuestion) return;
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: optionId }));
    setOtherSelected((prev) => ({ ...prev, [currentQuestion.id]: false }));
    setOtherText((prev) => ({ ...prev, [currentQuestion.id]: '' }));
  };

  const handleOtherToggle = () => {
    if (!currentQuestion) return;
    const isNowSelected = !otherSelected[currentQuestion.id];
    setOtherSelected((prev) => ({ ...prev, [currentQuestion.id]: isNowSelected }));
    if (isNowSelected) {
      setAnswers((prev) => ({ ...prev, [currentQuestion.id]: '' }));
    }
  };

  const handleOtherText = (text: string) => {
    if (!currentQuestion) return;
    setOtherText((prev) => ({ ...prev, [currentQuestion.id]: text }));
  };

  const handleTextInput = (text: string) => {
    if (!currentQuestion) return;
    setAnswers((prev) => ({ ...prev, [currentQuestion.id]: text }));
  };

  const handleNext = () => {
    if (!canProceed()) return;

    if (isLastStep) {
      handleSubmit();
    } else {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleSubmit = () => {
    if (isSubmitting) return;

    const finalAnswers: Record<string, string> = {};
    for (const q of questions.questions) {
      if (q.type === 'select') {
        const otherValue = otherText[q.id];
        const answerValue = answers[q.id];
        if (otherSelected[q.id] && otherValue?.trim()) {
          finalAnswers[q.id] = otherValue.trim();
        } else if (answerValue) {
          const option = q.options?.find((o) => o.id === answerValue);
          finalAnswers[q.id] = option?.label ?? answerValue;
        }
      } else {
        finalAnswers[q.id] = answers[q.id]?.trim() ?? '';
      }
    }

    onAnswer(finalAnswers);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canProceed()) {
      e.preventDefault();
      handleNext();
    }
  };

  if (!currentQuestion) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {/* Header with progress */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-100">
            <ChatBubbleIcon className="h-4 w-4 text-primary-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Quick question</h3>
          </div>
        </div>

        {/* Progress indicator */}
        {totalSteps > 1 && (
          <div className="flex items-center gap-1.5">
            {questions.questions.map((_, index) => (
              <button
                key={index}
                onClick={() => index < currentStep && setCurrentStep(index)}
                disabled={index > currentStep}
                className={`h-1.5 rounded-full transition-all ${
                  index === currentStep
                    ? 'w-4 bg-primary-500'
                    : index < currentStep
                    ? 'w-1.5 bg-primary-300 hover:bg-primary-400 cursor-pointer'
                    : 'w-1.5 bg-gray-200'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Question */}
      <div className="px-4 py-5">
        <p className="text-base font-medium text-gray-900">{currentQuestion.question}</p>
        {currentQuestion.required === false && (
          <span className="text-xs text-gray-400 mt-1">(optional)</span>
        )}

        {/* Answer input */}
        <div className="mt-4">
          {currentQuestion.type === 'text' ? (
            <input
              type="text"
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => handleTextInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={currentQuestion.placeholder || 'Type your answer...'}
              disabled={isSubmitting}
              autoFocus
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 disabled:opacity-50"
            />
          ) : (
            <div className="space-y-2">
              {currentQuestion.options?.map((option) => {
                const isSelected = answers[currentQuestion.id] === option.id && !otherSelected[currentQuestion.id];
                return (
                  <button
                    key={option.id}
                    onClick={() => handleSelectOption(option.id)}
                    disabled={isSubmitting}
                    className={`w-full rounded-lg border px-3 py-2.5 text-left transition-all ${
                      isSelected
                        ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-400'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                          isSelected
                            ? 'border-primary-500 bg-primary-500'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isSelected && (
                          <div className="h-1.5 w-1.5 rounded-full bg-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-sm text-gray-900">{option.label}</span>
                        {option.description && (
                          <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}

              {/* Other option */}
              <button
                onClick={handleOtherToggle}
                disabled={isSubmitting}
                className={`w-full rounded-lg border px-3 py-2.5 text-left transition-all ${
                  otherSelected[currentQuestion.id]
                    ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-400'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
                      otherSelected[currentQuestion.id]
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {otherSelected[currentQuestion.id] && (
                      <div className="h-1.5 w-1.5 rounded-full bg-white" />
                    )}
                  </div>
                  <span className="text-sm text-gray-900">Other</span>
                </div>
              </button>

              {/* Other text input */}
              {otherSelected[currentQuestion.id] && (
                <div className="mt-2 animate-fade-in">
                  <input
                    type="text"
                    value={otherText[currentQuestion.id] || ''}
                    onChange={(e) => handleOtherText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Please specify..."
                    disabled={isSubmitting}
                    autoFocus
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400 disabled:opacity-50"
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer with navigation */}
      <div className="flex items-center justify-between border-t border-gray-100 bg-gray-50 px-4 py-3">
        {/* Back button */}
        <div>
          {currentStep > 0 ? (
            <button
              onClick={handleBack}
              disabled={isSubmitting}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              Back
            </button>
          ) : (
            <div /> // Spacer
          )}
        </div>

        {/* Next/Submit button */}
        <button
          onClick={handleNext}
          disabled={!canProceed() || isSubmitting}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <SpinnerIcon className="h-4 w-4" />
              Submitting...
            </>
          ) : isLastStep ? (
            'Continue'
          ) : (
            <>
              Next
              <ArrowRightIcon className="h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}
